"use client";

import React, {
  useCallback,
  useEffect,
  useMemo,
  useState,
} from "react";
import axios from "axios";

import {
  Box,
  Autocomplete,
  Button,
  Chip,
  CircularProgress,
  Modal,
  FormControl,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Stack,
  Switch,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  TextField,
  Typography,
  Tooltip,
  Alert,
  Badge,
  IconButton,
  InputAdornment,
  useTheme,
} from "@mui/material";

import {
  Add,
  Delete,
  Edit,
  Visibility,
  Search,
  Refresh,
  Close,
  CheckCircle,
  Cancel,
  FilterList,
} from "@mui/icons-material";

import { styled } from "@mui/material/styles";

import BackButton from "../../ui/BackButton";
import { useBranch } from "./../../layouts/BranchContext";

/* =========================================================
   API CONFIG
========================================================= */

const API_BASE_URL = "https://wims.revisetoexcel.com/api";

const API = {
  list: `${API_BASE_URL}/library/fine-rule/list/`,

  create: `${API_BASE_URL}/library/fine-rule/create/`,

  detail: (id) =>
    `${API_BASE_URL}/library/fine-rule/${id}/`,

  update: (id) =>
    `${API_BASE_URL}/library/fine-rule/update/${id}/`,

  delete: (id) =>
    `${API_BASE_URL}/library/fine-rule/delete/${id}/`,

  status: (id) =>
    `${API_BASE_URL}/library/fine-rule/status/${id}/`,

  categories: `${API_BASE_URL}/library/book-category/dropdown/`,
};

/* =========================================================
   AXIOS
========================================================= */

const api = axios.create({
  baseURL: API_BASE_URL,
  headers: {
    "Content-Type": "application/json",
  },
});

api.interceptors.request.use(
  (config) => {
    if (typeof window !== "undefined") {
      const token =
        localStorage.getItem("accessToken") ||
        sessionStorage.getItem("accessToken") ||
        localStorage.getItem("access_token") ||
        sessionStorage.getItem("access_token") ||
        localStorage.getItem("token") ||
        sessionStorage.getItem("token");

      if (token) {
        config.headers.Authorization = `Bearer ${token}`;
      }
    }

    return config;
  },
  (error) => Promise.reject(error)
);

/* =========================================================
   SHARED SCROLLBAR
========================================================= */

const scrollbarStyles = (theme) => {
  const dark = theme.palette.mode === "dark";

  return {
    "&::-webkit-scrollbar": {
      width: "6px",
      height: "6px",
      background: "transparent",
    },

    "&::-webkit-scrollbar-thumb": {
      backgroundColor: dark
        ? "rgba(255,255,255,0.25)"
        : "rgba(0,0,0,0.20)",
      borderRadius: "5px",
    },

    "&::-webkit-scrollbar-thumb:hover": {
      backgroundColor: dark
        ? "rgba(255,255,255,0.40)"
        : "rgba(0,0,0,0.30)",
    },

    scrollbarWidth: "thin",
  };
};

const CustomScrollbar = styled("div")(
  ({ theme }) => ({
    overflowY: "auto",
    backgroundColor:
      theme.palette.background.paper,
    ...scrollbarStyles(theme),
  })
);

/* =========================================================
   HELPERS
========================================================= */

const getStoredUser = () => {
  if (typeof window === "undefined") {
    return {};
  }

  try {
    return JSON.parse(
      sessionStorage.getItem("user") || "{}"
    );
  } catch (error) {
    console.error(
      "Failed to parse stored user:",
      error
    );

    return {};
  }
};

const getArray = (data) => {
  if (Array.isArray(data)) {
    return data;
  }

  return (
    data?.results ||
    data?.data ||
    data?.items ||
    []
  );
};

const getErrorMessage = (
  error,
  fallback = "Something went wrong."
) => {
  const data = error?.response?.data;

  if (error?.response?.status === 401) {
    return "Authentication failed. Please login again.";
  }

  if (error?.response?.status === 403) {
    return "You do not have permission to access this API.";
  }

  if (error?.response?.status === 404) {
    return "API endpoint not found (404). Please check the backend API URL.";
  }

  if (!data) {
    return error?.message || fallback;
  }

  if (typeof data === "string") {
    return data;
  }

  if (data.detail) {
    return String(data.detail);
  }

  if (data.message) {
    return typeof data.message === "string"
      ? data.message
      : JSON.stringify(data.message);
  }

  if (typeof data === "object") {
    return Object.entries(data)
      .map(([key, value]) => {
        const message = Array.isArray(value)
          ? value.join(", ")
          : typeof value === "object"
          ? JSON.stringify(value)
          : value;

        return `${key}: ${message}`;
      })
      .join(" | ");
  }

  return fallback;
};

const getCategoryLabel = (item) =>
  item?.name ||
  item?.category_name ||
  item?.label ||
  `Category ${item?.id}`;

const createEmptyFilters = () => ({
  member_type: "",
  category: "",
  is_active: "",
});

/* =========================================================
   COMPONENT
========================================================= */

export default function ManageFine() {
  const theme = useTheme();

  const isDark = theme.palette.mode === "dark";

  // Global branch selector (navbar)
  const { selectedBranchId } = useBranch();

  const user = useMemo(() => getStoredUser(), []);

  const organizationId = user?.organization_id
    ? String(user.organization_id)
    : "";

  /* =========================================================
     COLORS
  ========================================================= */

  const textColor = theme.palette.text.primary;

  const secondaryTextColor = theme.palette.text.disabled;

  const modalTextColor = isDark ? "#ffffff" : "#000000";

  const borderColor = theme.palette.divider;

  const tableRowHover = isDark
    ? "rgba(255,255,255,0.05)"
    : "rgba(0,0,0,0.025)";

  const modalInputBg = isDark
    ? "rgba(255,255,255,0.03)"
    : "#ffffff";

  /* =========================================================
     SELECT STYLE
  ========================================================= */

 const outlineColor = isDark ? "rgba(255,255,255,0.35)" : "rgba(0,0,0,0.30)";

const inputLabelSx = {
  color: modalTextColor,
  fontWeight: 500,

  "&.Mui-focused": {
    color: theme.palette.primary.main,
  },

  "&.Mui-disabled": {
    color: modalTextColor,
    opacity: 0.8,
  },
};

const selectSx = {
  minHeight: 46,
  borderRadius: "8px",
  color: modalTextColor,
  backgroundColor: modalInputBg,

  "& .MuiSelect-select": {
    minHeight: "46px !important",
    display: "flex",
    alignItems: "center",
    boxSizing: "border-box",
    padding: "8px 35px 8px 14px !important",
    color: `${modalTextColor} !important`,
    WebkitTextFillColor: modalTextColor,
  },

  "& .MuiSelect-select.Mui-disabled": {
    color: `${modalTextColor} !important`,
    WebkitTextFillColor: `${modalTextColor} !important`,
    opacity: 0.85,
  },

  "& .MuiOutlinedInput-notchedOutline": {
    borderColor: outlineColor,
  },

  "&:hover .MuiOutlinedInput-notchedOutline": {
    borderColor: theme.palette.primary.main,
  },

  "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
    borderWidth: 1.5,
    borderColor: theme.palette.primary.main,
  },

  "&.Mui-disabled .MuiOutlinedInput-notchedOutline": {
    borderColor: outlineColor,
  },

  "& .MuiSvgIcon-root": {
    color: modalTextColor,
  },

  "&.Mui-disabled .MuiSvgIcon-root": {
    color: modalTextColor,
    opacity: 0.6,
  },
};

const textFieldSx = {
  "& .MuiOutlinedInput-root": {
    minHeight: 46,
    borderRadius: "8px",
    backgroundColor: modalInputBg,

    "& fieldset": {
      borderColor: outlineColor,
    },

    "&:hover fieldset": {
      borderColor: theme.palette.primary.main,
    },

    "&.Mui-focused fieldset": {
      borderColor: theme.palette.primary.main,
      borderWidth: 1.5,
    },

    "&.Mui-disabled fieldset": {
      borderColor: outlineColor,
    },
  },

  "& .MuiInputLabel-root": {
    color: modalTextColor,
  },

  "& .MuiInputLabel-root.Mui-focused": {
    color: theme.palette.primary.main,
  },

  "& .MuiInputLabel-root.Mui-disabled": {
    color: modalTextColor,
    opacity: 0.8,
  },

  "& .MuiInputBase-input": {
    color: modalTextColor,
    WebkitTextFillColor: modalTextColor,
  },

  "& .MuiInputBase-input.Mui-disabled": {
    color: modalTextColor,
    WebkitTextFillColor: modalTextColor,
    opacity: 0.85,
  },
};

  const menuProps = {
    PaperProps: {
      sx: {
        maxHeight: 320,
        backgroundColor: theme.palette.background.paper,
        color: modalTextColor,

        ...scrollbarStyles(theme),

        "& .MuiMenuItem-root": {
          color: modalTextColor,
        },

        "& .MuiMenuItem-root:hover": {
          backgroundColor: tableRowHover,
        },

        "& .MuiMenuItem-root.Mui-selected": {
          backgroundColor: theme.palette.action.selected,
        },
      },
    },
  };

  /* =========================================================
     TABLE STATES

     Swagger /list/ returns a plain array
     (no page / page_size / search params) —
     pagination + search are done client-side.
  ========================================================= */

  const [allRows, setAllRows] = useState([]);

  const [loading, setLoading] = useState(false);

  const [page, setPage] = useState(0);

  const [rowsPerPage, setRowsPerPage] = useState(10);

  /* CLIENT-SIDE SEARCH */

  const [searchText, setSearchText] = useState("");

  const rows = useMemo(() => {
    const keyword = searchText.trim().toLowerCase();

    const filtered = keyword
      ? allRows.filter((row) => {
          const searchable = [
            row.organization_name,
            row.branch_name,
            row.member_type_display,
            row.member_type,
            row.category_name,
            String(row.grace_days ?? ""),
            String(row.fine_per_day ?? ""),
            String(row.maximum_fine ?? ""),
          ]
            .filter(Boolean)
            .join(" ")
            .toLowerCase();

          return searchable.includes(keyword);
        })
      : allRows;

    const start = page * rowsPerPage;

    return filtered.slice(start, start + rowsPerPage);
  }, [allRows, searchText, page, rowsPerPage]);

  const filteredCount = useMemo(() => {
    const keyword = searchText.trim().toLowerCase();

    if (!keyword) return allRows.length;

    return allRows.filter((row) => {
      const searchable = [
        row.organization_name,
        row.branch_name,
        row.member_type_display,
        row.member_type,
        row.category_name,
        String(row.grace_days ?? ""),
        String(row.fine_per_day ?? ""),
        String(row.maximum_fine ?? ""),
      ]
        .filter(Boolean)
        .join(" ")
        .toLowerCase();

      return searchable.includes(keyword);
    }).length;
  }, [allRows, searchText]);

  /* =========================================================
     FILTERS

     Swagger list filters:
     organization, branch, member_type,
     category, is_active
  ========================================================= */

  const [filters, setFilters] = useState(
    createEmptyFilters()
  );

  const [filterDraft, setFilterDraft] = useState(
    createEmptyFilters()
  );

  const [filterOpen, setFilterOpen] = useState(false);

  /* =========================================================
     DROPDOWN DATA
  ========================================================= */

  const [categories, setCategories] = useState([]);

  /* =========================================================
     ADD / EDIT FORM
  ========================================================= */

  const getEmptyForm = () => ({
    member_type: "",
    category: "",
    grace_days: 0,
    fine_per_day: "",
    maximum_fine: "",
    is_active: true,
  });

  const [form, setForm] = useState(getEmptyForm);

  const [openDialog, setOpenDialog] = useState(false);

  const [dialogMode, setDialogMode] = useState("create");

  const [selectedId, setSelectedId] = useState(null);

  const [formLoading, setFormLoading] = useState(false);

  /* =========================================================
     ACTION CONFIRMATION MODAL

     Used for:
     - Delete
     - Activate
     - Deactivate
  ========================================================= */

  const [actionModalOpen, setActionModalOpen] =
    useState(false);

  const [actionType, setActionType] = useState("");

  const [actionRow, setActionRow] = useState(null);

  const [actionLoading, setActionLoading] = useState(false);

  /* =========================================================
     MESSAGES
  ========================================================= */

  const [error, setError] = useState("");

  const [success, setSuccess] = useState("");

  /* =========================================================
     ACTIVE FILTER COUNT
  ========================================================= */

  const activeFilterCount = Object.values(
    filters
  ).filter(
    (value) =>
      value !== "" &&
      value !== null &&
      value !== undefined
  ).length;

  /* =========================================================
     FETCH CATEGORIES

     GET /library/book-category/dropdown/
     Scoped to selected branch.
  ========================================================= */

  const fetchCategories = useCallback(
    async (branchId = selectedBranchId) => {
      if (!organizationId || !branchId) {
        setCategories([]);
        return;
      }

      try {
        const params = {
          organization: organizationId,
          branch: branchId,
        };

        const response = await api.get(API.categories, {
          params,
        });

        setCategories(getArray(response.data));
      } catch (err) {
        console.error("Category error:", err);
        setCategories([]);
      }
    },
    [organizationId, selectedBranchId]
  );

  /* =========================================================
     FETCH FINE RULES

     GET /library/fine-rule/list/
     Filters (Swagger):
     organization, branch, member_type,
     category, is_active

     NOTE: no server-side search / pagination —
     the full filtered list is loaded and paginated
     client-side.
  ========================================================= */

  const fetchFineRules = useCallback(
    async ({
      filterValues = filters,
      branch = selectedBranchId,
    } = {}) => {
      if (!organizationId || !branch) {
        setAllRows([]);
        return;
      }

      try {
        setLoading(true);
        setError("");

        const params = {
          organization: organizationId,
          branch: branch,
        };

        Object.entries(filterValues).forEach(
          ([key, value]) => {
            if (
              value !== "" &&
              value !== null &&
              value !== undefined
            ) {
              params[key] = value;
            }
          }
        );

        const response = await api.get(API.list, {
          params,
        });

        setAllRows(getArray(response.data));
      } catch (err) {
        console.error("Fine Rule List Error:", err);

        setAllRows([]);

        setError(
          getErrorMessage(err, "Failed to fetch fine rules.")
        );
      } finally {
        setLoading(false);
      }
    },
    [organizationId, selectedBranchId, filters]
  );

  /* =========================================================
     BRANCH CHANGE / INITIAL LOAD
  ========================================================= */

  useEffect(() => {
    setPage(0);

    fetchFineRules({ currentPage: 0 });

    fetchCategories();
  }, [selectedBranchId]);

  /* =========================================================
     SEARCH (client-side)
  ========================================================= */

  const handleSearchKeyDown = (event) => {
    if (event.key === "Enter") {
      setPage(0);
    }
  };

  const handleClearSearch = () => {
    setSearchText("");
    setPage(0);
  };

  /* =========================================================
     FILTER MODAL
  ========================================================= */

  const handleOpenFilters = () => {
    setFilterDraft({ ...filters });

    setFilterOpen(true);
  };

  const handleCloseFilters = () => {
    setFilterDraft({ ...filters });

    setFilterOpen(false);
  };

  const handleFilterChange = (name, value) => {
    setFilterDraft((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  /* =========================================================
     APPLY FILTERS
  ========================================================= */

  const handleApplyFilters = async () => {
    const applied = { ...filterDraft };

    setFilters(applied);

    setPage(0);

    setFilterOpen(false);

    await fetchFineRules({
      filterValues: applied,
      currentPage: 0,
    });
  };

  /* =========================================================
     RESET FILTERS
  ========================================================= */

  const handleResetFilters = async () => {
    const reset = createEmptyFilters();

    setFilterDraft(reset);

    setFilters(reset);

    setPage(0);

    setFilterOpen(false);

    await fetchFineRules({
      filterValues: reset,
      currentPage: 0,
    });
  };

  /* =========================================================
     FORM CHANGE
  ========================================================= */

  const handleFormChange = (name, value) => {
    setForm((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  /* =========================================================
     CREATE MODAL
  ========================================================= */

  const handleCreate = () => {
    setError("");
    setSuccess("");

    setForm(getEmptyForm());

    fetchCategories();

    setSelectedId(null);

    setDialogMode("create");

    setOpenDialog(true);
  };

  /* =========================================================
     DETAIL

     GET /library/fine-rule/{id}/
  ========================================================= */

  const fetchDetail = async (id, mode = "view") => {
    try {
      setFormLoading(true);
      setError("");

      const response = await api.get(API.detail(id));

      const data = response.data?.data ?? response.data;

      setForm({
        member_type: data.member_type ?? "",
        category:
          data.category === null ||
          data.category === undefined
            ? ""
            : String(data.category),
        grace_days: data.grace_days ?? 0,
        fine_per_day: data.fine_per_day ?? "",
        maximum_fine: data.maximum_fine ?? "",
        is_active: data.is_active ?? true,
      });

      fetchCategories();

      setSelectedId(id);

      setDialogMode(mode);

      setOpenDialog(true);
    } catch (err) {
      console.error("Detail error:", err);

      setError(
        getErrorMessage(err, "Failed to fetch fine rule details.")
      );
    } finally {
      setFormLoading(false);
    }
  };

  /* =========================================================
     VALIDATION
  ========================================================= */

  const validateForm = () => {
    if (!selectedBranchId) {
      return "Please select a branch.";
    }

    if (!form.member_type) {
      return "Please select member type.";
    }

    if (
      form.fine_per_day === "" ||
      form.fine_per_day === null
    ) {
      return "Please enter fine per day.";
    }

    if (
      form.maximum_fine === "" ||
      form.maximum_fine === null
    ) {
      return "Please enter maximum fine.";
    }

    if (Number(form.fine_per_day) < 0) {
      return "Fine per day cannot be negative.";
    }

    if (Number(form.maximum_fine) < 0) {
      return "Maximum fine cannot be negative.";
    }

    if (
      Number(form.maximum_fine) > 0 &&
      Number(form.maximum_fine) < Number(form.fine_per_day)
    ) {
      return "Maximum fine cannot be less than fine per day.";
    }

    if (Number(form.grace_days || 0) < 0) {
      return "Grace days cannot be negative.";
    }

    return "";
  };

  /* =========================================================
     CREATE

     POST /library/fine-rule/create/
     Payload (Swagger):
     {
       organization, branch, member_type,
       category, grace_days,
       fine_per_day, maximum_fine
     }

     One active rule allowed per
     Organization + Branch + Member Type + Category.
     Blank category = applies to all categories.
  ========================================================= */

  const handleSubmit = async () => {
    const validationError = validateForm();

    if (validationError) {
      setError(validationError);

      return;
    }

    try {
      setFormLoading(true);
      setError("");
      setSuccess("");

      const payload = {
        organization: Number(organizationId),
        branch: Number(selectedBranchId),
        member_type: form.member_type,
        category: form.category
          ? Number(form.category)
          : null,
        grace_days: Number(form.grace_days || 0),
        fine_per_day: String(form.fine_per_day),
        maximum_fine: String(form.maximum_fine),
      };

      await api.post(API.create, payload);

      setSuccess("Fine rule created successfully.");

      setOpenDialog(false);

      await fetchFineRules();
    } catch (err) {
      console.error("Create Fine Rule Error:", err);

      setError(
        getErrorMessage(err, "Failed to create fine rule.")
      );
    } finally {
      setFormLoading(false);
    }
  };

  /* =========================================================
     UPDATE

     PUT /library/fine-rule/update/{id}/
     Payload (Swagger):
     {
       is_active, member_type, grace_days,
       fine_per_day, maximum_fine,
       organization, branch, category
     }
  ========================================================= */

  const handleUpdate = async () => {
    const validationError = validateForm();

    if (validationError) {
      setError(validationError);

      return;
    }

    try {
      setFormLoading(true);
      setError("");
      setSuccess("");

      const payload = {
        organization: Number(organizationId),
        branch: Number(selectedBranchId),
        member_type: form.member_type,
        category: form.category
          ? Number(form.category)
          : null,
        grace_days: Number(form.grace_days || 0),
        fine_per_day: String(form.fine_per_day),
        maximum_fine: String(form.maximum_fine),
        is_active: Boolean(form.is_active),
      };

      await api.put(API.update(selectedId), payload);

      setSuccess("Fine rule updated successfully.");

      setOpenDialog(false);

      await fetchFineRules();
    } catch (err) {
      console.error("Update Fine Rule Error:", err);

      setError(
        getErrorMessage(err, "Failed to update fine rule.")
      );
    } finally {
      setFormLoading(false);
    }
  };

  /* =========================================================
     OPEN ACTION MODAL

     DELETE / ACTIVATE / DEACTIVATE
  ========================================================= */

  const handleDelete = (row) => {
    setActionRow(row);
    setActionType("delete");
    setActionModalOpen(true);
  };

  const handleStatus = (row) => {
    setActionRow(row);
    setActionType("status");
    setActionModalOpen(true);
  };

  /* =========================================================
     CONFIRM ACTION

     DELETE /library/fine-rule/delete/{id}/  (soft delete)
     PATCH /library/fine-rule/status/{id}/
  ========================================================= */

  const handleConfirmAction = async () => {
    if (!actionRow) {
      return;
    }

    try {
      setActionLoading(true);
      setError("");

      if (actionType === "delete") {
        await api.delete(API.delete(actionRow.id));

        setSuccess("Fine rule deleted successfully.");
      }

      if (actionType === "status") {
        await api.patch(API.status(actionRow.id), {
          is_active: !actionRow.is_active,
        });

        setSuccess(
          `Fine rule ${
            !actionRow.is_active ? "activated" : "deactivated"
          } successfully.`
        );
      }

      setActionModalOpen(false);
      setActionRow(null);
      setActionType("");

      await fetchFineRules();
    } catch (err) {
      console.error("Action error:", err);

      setError(
        getErrorMessage(err, "Failed to perform action.")
      );
    } finally {
      setActionLoading(false);
    }
  };

  /* =========================================================
     CLOSE ACTION MODAL
  ========================================================= */

  const handleCloseActionModal = () => {
    if (actionLoading) {
      return;
    }

    setActionModalOpen(false);
    setActionRow(null);
    setActionType("");
  };

  /* =========================================================
     RENDER
  ========================================================= */

  return (
    <Box
      sx={{
        p: { xs: 1.5, sm: 2, md: 3 },
      }}
    >
      {/* =====================================================
          HEADER
      ===================================================== */}

      <Box
        display="flex"
        justifyContent="space-between"
        alignItems="center"
        gap={2}
        mb={3}
        flexWrap="wrap"
      >
        <Stack
          direction={{ xs: "column", sm: "row" }}
          spacing={1}
          alignItems={{ xs: "stretch", sm: "center" }}
          width={{ xs: "100%", sm: "auto" }}
        >


{/* SEARCH (client-side) */}
<TextField
  variant="outlined"
  placeholder="Search fine rules..."
  value={searchText}
  onChange={(e) => {
    setSearchText(e.target.value);
    setPage(0);
  }}
  onKeyDown={handleSearchKeyDown}
  sx={{
    width: 350,
    maxWidth: "100%",
    minWidth: { xs: "100%", sm: 240 },

    "& .MuiOutlinedInput-root": {
      height: 50,
      borderRadius: "30px",
      backgroundColor:
        theme.palette.mode === "light"
          ? "#ffffff"
          : "rgba(255, 255, 255, 0.05)",

      "& fieldset": {
        borderColor:
          theme.palette.mode === "light"
            ? "rgba(0, 0, 0, 0.23)"
            : "rgba(255, 255, 255, 0.35)",
      },

      "&:hover fieldset": {
        borderColor: theme.palette.primary.main,
      },

      "&.Mui-focused fieldset": {
        borderColor: theme.palette.primary.main,
        borderWidth: 2,
      },
    },

    // Typed text
    "& .MuiInputBase-input": {
      color: theme.palette.text.primary,
      WebkitTextFillColor: theme.palette.text.primary,
      padding: "0 8px",
    },

    // Placeholder text
    "& .MuiInputBase-input::placeholder": {
      color: theme.palette.text.disabled,
      opacity: 1,
      WebkitTextFillColor: theme.palette.text.disabled,
    },
  }}
  InputProps={{
    startAdornment: (
      <InputAdornment position="start">
        <Search
          sx={{
            color: theme.palette.primary.main,
            ml: 1,
          }}
        />
      </InputAdornment>
    ),

    endAdornment: searchText ? (
      <InputAdornment position="end">
        <IconButton
          size="small"
          onClick={handleClearSearch}
          aria-label="Clear search"
        >
          <Close
            fontSize="small"
            sx={{
              color: theme.palette.text.disabled,
            }}
          />
        </IconButton>
      </InputAdornment>
    ) : null,
  }}
/>

          {/* FILTER */}

          <Button
            variant="contained"
            startIcon={
              <Badge
                badgeContent={activeFilterCount}
                color="primary"
              >
                <FilterList />
              </Badge>
            }
            onClick={handleOpenFilters}
            sx={{
              textTransform: "none",
              minWidth: 105,
              height: 40,
              borderRadius: 1.5,
              fontWeight: 600,
            }}
          >
            Filter
          </Button>

          {/* ADD */}

          <Button
            variant="contained"
            startIcon={<Add />}
            onClick={handleCreate}
            sx={{
              textTransform: "none",
              borderRadius: 1.5,
              height: 40,
            }}
          >
            Add Fine Rule
          </Button>
        </Stack>
      </Box>

      {/* ALERTS */}

      {error && (
        <Alert
          severity="error"
          sx={{ mb: 2 }}
          onClose={() => setError("")}
        >
          {error}
        </Alert>
      )}

      {success && (
        <Alert
          severity="success"
          sx={{
            mb: 2,

            ...(isDark && {
              backgroundColor: "#ffffff",
              color: "#111827",

              "& .MuiAlert-icon": {
                color: theme.palette.success.main,
              },

              "& .MuiAlert-message": {
                color: "#111827",
                fontWeight: 600,
              },
            }),
          }}
          onClose={() => setSuccess("")}
        >
          {success}
        </Alert>
      )}

      {/* =====================================================
          TABLE
      ===================================================== */}

      <Paper
        elevation={3}
        sx={{
          borderRadius: "16px",
          overflow: "hidden",
          backgroundColor: theme.palette.background.paper,
          color: textColor,
          border: `1px solid ${
            isDark
              ? "rgba(255,255,255,0.08)"
              : "rgba(0,0,0,0.08)"
          }`,
        }}
      >
        <TableContainer
          sx={{
            overflow: "auto",
            ...scrollbarStyles(theme),
          }}
        >
          <Table
            stickyHeader
            sx={{
              minWidth: 1100,

              "& .MuiTableCell-root": {
                color: `${textColor} !important`,
                borderColor: theme.palette.divider,
              },

              "& .MuiTableBody-root .MuiTableRow-root": {
                backgroundColor: theme.palette.background.paper,
              },

              "& .MuiTableBody-root .MuiTableRow-root:hover": {
                backgroundColor: tableRowHover,
              },

              "& .MuiTableHead-root .MuiTableCell-root": {
                fontWeight: 700,
                backgroundColor: theme.palette.primary.main,
                color: `${theme.palette.primary.contrastText} !important`,
                whiteSpace: "nowrap",
              },
            }}
          >
            <TableHead>
              <TableRow>
                <TableCell>S.No</TableCell>

                <TableCell>Branch</TableCell>

                <TableCell>Member Type</TableCell>

                <TableCell>Category</TableCell>

                <TableCell>Grace Days</TableCell>

                <TableCell>Fine / Day</TableCell>

                <TableCell>Maximum Fine</TableCell>

                <TableCell>Status</TableCell>

                <TableCell align="center">
                  Action
                </TableCell>
              </TableRow>
            </TableHead>

            <TableBody>
              {loading ? (
                <TableRow>
                  <TableCell
                    colSpan={9}
                    align="center"
                    sx={{ py: 5 }}
                  >
                    <CircularProgress size={28} />
                  </TableCell>
                </TableRow>
              ) : rows.length > 0 ? (
                rows.map((row, index) => (
                  <TableRow key={row.id} hover>
                    <TableCell>
                      {page * rowsPerPage + index + 1}
                    </TableCell>

                    <TableCell>
                      {row.branch_name || "-"}
                    </TableCell>

                    <TableCell>
                      <Chip
                        size="small"
                        label={
                          row.member_type_display ||
                          row.member_type ||
                          "-"
                        }
                        color={
                          row.member_type === "student"
                            ? "info"
                            : "success"
                        }
                        variant="contained"
                        sx={{ fontWeight: 600 }}
                      />
                    </TableCell>

                    <TableCell>
                      {row.category_name ? (
                        <Chip
                          size="small"
                          label={row.category_name}
                          color="success"
                          variant="contained"
                        />
                      ) : (
                        <Typography
                          variant="body2"
                          color={secondaryTextColor}
                        >
                          All Categories
                        </Typography>
                      )}
                    </TableCell>

                    <TableCell>
                      {row.grace_days ?? 0}
                    </TableCell>

                    <TableCell>
                      ₹ {row.fine_per_day ?? "0"}
                    </TableCell>

                    <TableCell>
                      ₹ {row.maximum_fine ?? "0"}
                    </TableCell>

                    <TableCell>
                      <Chip
                        size="small"
                        icon={
                          row.is_active ? (
                            <CheckCircle />
                          ) : (
                            <Cancel />
                          )
                        }
                        label={
                          row.is_active ? "Active" : "Inactive"
                        }
                        sx={{
                          fontWeight: 600,
                          borderRadius: "20px",

                          backgroundColor: row.is_active
                            ? "rgba(46,125,50,0.12)"
                            : "rgba(211,47,47,0.12)",

                          color: row.is_active
                            ? theme.palette.success.main
                            : theme.palette.error.main,

                          "& .MuiChip-icon": {
                            color: row.is_active
                              ? theme.palette.success.main
                              : theme.palette.error.main,
                          },

                          border: "none",
                        }}
                      />
                    </TableCell>

                    <TableCell align="center">
                      <Stack
                        direction="row"
                        justifyContent="center"
                        spacing={0.5}
                      >
                        {/* VIEW */}

                        <Tooltip title="View">
                          <IconButton
                            color="info"
                            onClick={() =>
                              fetchDetail(row.id, "view")
                            }
                          >
                            <Visibility />
                          </IconButton>
                        </Tooltip>

                        {/* EDIT */}

                        <Tooltip title="Edit">
                          <IconButton
                            color="primary"
                            onClick={() =>
                              fetchDetail(row.id, "edit")
                            }
                          >
                            <Edit />
                          </IconButton>
                        </Tooltip>

                        {/* ACTIVATE / DEACTIVATE */}

                        <Tooltip
                          title={
                            row.is_active
                              ? "Deactivate"
                              : "Activate"
                          }
                        >
                          <IconButton
                            color={
                              row.is_active
                                ? "warning"
                                : "success"
                            }
                            onClick={() => handleStatus(row)}
                          >
                            {row.is_active ? (
                              <Cancel />
                            ) : (
                              <CheckCircle />
                            )}
                          </IconButton>
                        </Tooltip>

                        {/* DELETE */}

                        <Tooltip title="Delete">
                          <IconButton
                            color="error"
                            onClick={() => handleDelete(row)}
                          >
                            <Delete />
                          </IconButton>
                        </Tooltip>
                      </Stack>
                    </TableCell>
                  </TableRow>
                ))
              ) : (
                <TableRow>
                  <TableCell
                    colSpan={9}
                    align="center"
                    sx={{
                      py: 5,
                      color: `${secondaryTextColor} !important`,
                    }}
                  >
                    No fine rules found.
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>

        <TablePagination
          component="div"
          count={filteredCount}
          page={page}
          onPageChange={(_event, newPage) => setPage(newPage)}
          rowsPerPage={rowsPerPage}
          onRowsPerPageChange={(event) => {
            const value = parseInt(event.target.value, 10);

            setRowsPerPage(value);
            setPage(0);
          }}
          rowsPerPageOptions={[10, 25, 50, 100]}
          sx={{
            color: textColor,

            "& .MuiTablePagination-selectLabel": {
              color: textColor,
            },

            "& .MuiTablePagination-displayedRows": {
              color: textColor,
            },

            "& .MuiSelect-select": {
              color: textColor,
            },

            "& .MuiSvgIcon-root": {
              color: textColor,
            },
          }}
        />
      </Paper>

      {/* =====================================================
          FILTER MODAL
      ===================================================== */}

      <Modal
        open={filterOpen}
        onClose={handleCloseFilters}
      >
        <Box
          sx={{
            position: "absolute",
            top: "50%",
            left: "50%",
            transform: "translate(-50%, -50%)",
            width: { xs: "95%", sm: 600 },
            maxWidth: "95%",
            maxHeight: "90vh",
            bgcolor: theme.palette.background.paper,
            color: modalTextColor,
            borderRadius: 3,
            boxShadow: 24,
            overflow: "hidden",
            outline: "none",
            display: "flex",
            flexDirection: "column",
          }}
        >
          {/* HEADER */}

          <Box
            sx={{
              backgroundColor: theme.palette.primary.main,
              px: 2.5,
              py: 1.75,
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              flexShrink: 0,
            }}
          >
            <Typography
              variant="h6"
              sx={{
                color: "#ffffff !important",
                fontWeight: 700,
              }}
            >
              Filters
            </Typography>

            <IconButton
              onClick={handleCloseFilters}
              sx={{ color: "#ffffff" }}
            >
              <Close />
            </IconButton>
          </Box>

          {/* BODY */}

          <CustomScrollbar
            sx={{
              p: 3,
              maxHeight: "calc(90vh - 135px)",
            }}
          >
            <Box
              sx={{
                display: "grid",
                gridTemplateColumns: {
                  xs: "1fr",
                  sm: "repeat(2, minmax(0, 1fr))",
                },
                gap: 2,
              }}
            >
              {/* MEMBER TYPE */}

              <FormControl fullWidth size="small">
                <InputLabel sx={inputLabelSx}>
                  Member Type
                </InputLabel>

                <Select
                  label="Member Type"
                  value={filterDraft.member_type}
                  onChange={(e) =>
                    handleFilterChange(
                      "member_type",
                      e.target.value
                    )
                  }
                  sx={selectSx}
                  MenuProps={menuProps}
                >
                  <MenuItem value="">
                    All Member Types
                  </MenuItem>

                  <MenuItem value="student">
                    Student
                  </MenuItem>

                  <MenuItem value="staff">
                    Staff
                  </MenuItem>
                </Select>
              </FormControl>

              {/* CATEGORY */}

              <Autocomplete
                fullWidth
                size="small"
                options={[
                  { id: "", label: "All Categories" },
                  ...categories,
                ]}
                value={
                  [
                    { id: "", label: "All Categories" },
                    ...categories,
                  ].find(
                    (category) =>
                      String(category.id) ===
                      String(filterDraft.category || "")
                  ) || null
                }
                onChange={(_, category) =>
                  handleFilterChange(
                    "category",
                    category?.id ? String(category.id) : ""
                  )
                }
                getOptionLabel={(category) =>
                  getCategoryLabel(category)
                }
                isOptionEqualToValue={(option, value) =>
                  String(option.id) === String(value.id)
                }
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="Category"
                    placeholder="Search categories..."
                    sx={{
                      "& .MuiInputLabel-root": inputLabelSx,
                    }}
                  />
                )}
                sx={selectSx}
              />

              {/* STATUS */}

              <FormControl fullWidth size="small">
                <InputLabel sx={inputLabelSx}>
                  Status
                </InputLabel>

                <Select
                  label="Status"
                  value={filterDraft.is_active}
                  onChange={(e) =>
                    handleFilterChange(
                      "is_active",
                      e.target.value
                    )
                  }
                  sx={selectSx}
                  MenuProps={menuProps}
                >
                  <MenuItem value="">All Status</MenuItem>

                  <MenuItem value="true">Active</MenuItem>

                  <MenuItem value="false">Inactive</MenuItem>
                </Select>
              </FormControl>
            </Box>
          </CustomScrollbar>

          {/* FOOTER */}

          <Box
            sx={{
              display: "flex",
              justifyContent: "flex-end",
              gap: 1,
              p: 2,
              borderTop: `1px solid ${borderColor}`,
              flexShrink: 0,
            }}
          >
            <Button
              variant="contained"
              startIcon={<Refresh />}
              onClick={handleResetFilters}
              sx={{ textTransform: "none" }}
            >
              RESET
            </Button>

            <Button
              variant="contained"
              onClick={handleCloseFilters}
              sx={{ textTransform: "none" }}
            >
              CANCEL
            </Button>

            <Button
              variant="contained"
              startIcon={<Search />}
              onClick={handleApplyFilters}
              sx={{ textTransform: "none" }}
            >
              APPLY FILTERS
            </Button>
          </Box>
        </Box>
      </Modal>

      {/* =====================================================
          ADD / VIEW / EDIT MODAL
      ===================================================== */}

      <Modal
        open={openDialog}
        onClose={() => !formLoading && setOpenDialog(false)}
      >
        <Box
          sx={{
            position: "absolute",
            top: "50%",
            left: "50%",
            transform: "translate(-50%, -50%)",
            width: { xs: "95%", sm: 600, md: 850 },
            maxWidth: "95%",
            maxHeight: "90vh",
            bgcolor: theme.palette.background.paper,
            color: modalTextColor,
            borderRadius: 3,
            boxShadow: 24,
            overflow: "hidden",
            outline: "none",
            display: "flex",
            flexDirection: "column",
          }}
        >
          {/* HEADER */}

          <Box
            sx={{
              backgroundColor: theme.palette.primary.main,
              px: 2,
              py: 1.5,
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              flexShrink: 0,
            }}
          >
            <Box>
              <Typography
                variant="h6"
                sx={{
                  color: "#ffffff !important",
                  fontWeight: 700,
                }}
              >
                {dialogMode === "create"
                  ? "Add Fine Rule"
                  : dialogMode === "edit"
                  ? "Edit Fine Rule"
                  : "Fine Rule Details"}
              </Typography>

              <Typography
                variant="body2"
                sx={{
                  color: "rgba(255,255,255,0.85) !important",
                  mt: 0.3,
                }}
              >
                {dialogMode === "create"
                  ? "Create a new library fine rule."
                  : "View and manage library fine rule details."}
              </Typography>
            </Box>

            <IconButton
              disabled={formLoading}
              onClick={() => setOpenDialog(false)}
              sx={{ color: "#ffffff" }}
            >
              <Close />
            </IconButton>
          </Box>

          {/* BODY */}

          <CustomScrollbar
            sx={{
              p: 3,
              maxHeight: "calc(90vh - 135px)",
            }}
          >
            {formLoading ? (
              <Box
                minHeight={300}
                display="flex"
                justifyContent="center"
                alignItems="center"
              >
                <CircularProgress />
              </Box>
            ) : (
              <>
                <Box
                  sx={{
                    display: "grid",
                    gridTemplateColumns: {
                      xs: "1fr",
                      sm: "repeat(2, minmax(0, 1fr))",
                    },
                    gap: 2,
                  }}
                >
                  {/* MEMBER TYPE */}

                  <FormControl
                    fullWidth
                    size="small"
                  >
                    <InputLabel sx={inputLabelSx}>
                      Member Type *
                    </InputLabel>

                    <Select
                      label="Member Type *"
                      value={form.member_type || ""}
                      disabled={
                        dialogMode === "view" || formLoading
                      }
                      onChange={(e) =>
                        handleFormChange(
                          "member_type",
                          e.target.value
                        )
                      }
                      sx={selectSx}
                      MenuProps={menuProps}
                    >
                      <MenuItem value="">
                        Select Member Type
                      </MenuItem>

                      <MenuItem value="student">
                        Student
                      </MenuItem>

                      <MenuItem value="staff">
                        Staff
                      </MenuItem>
                    </Select>
                  </FormControl>

                  {/* CATEGORY
                      Blank = rule applies to all
                      book categories (per Swagger). */}

                  <Autocomplete
                    fullWidth
                    size="small"
                    options={[
                      { id: "", label: "All Categories" },
                      ...categories,
                    ]}
                    value={
                      [
                        { id: "", label: "All Categories" },
                        ...categories,
                      ].find(
                        (category) =>
                          String(category.id) ===
                          String(form.category || "")
                      ) || null
                    }
                    onChange={(_, category) =>
                      handleFormChange(
                        "category",
                        category?.id ? String(category.id) : ""
                      )
                    }
                    getOptionLabel={(category) =>
                      getCategoryLabel(category)
                    }
                    isOptionEqualToValue={(option, value) =>
                      String(option.id) === String(value.id)
                    }
                    disabled={
                      dialogMode === "view" || formLoading
                    }
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        label="Category"
                        placeholder="Search categories..."
                        sx={{
                          "& .MuiInputLabel-root": inputLabelSx,
                        }}
                      />
                    )}
                    sx={selectSx}
                  />

                  {/* GRACE DAYS */}

                  <TextField
                    fullWidth
                    size="small"
                    type="number"
                    label="Grace Days"
                    value={form.grace_days}
                    disabled={
                      dialogMode === "view" || formLoading
                    }
                    inputProps={{ min: 0 }}
                    onChange={(e) =>
                      handleFormChange(
                        "grace_days",
                        e.target.value
                      )
                    }
                    sx={textFieldSx}
                  />

                  {/* FINE PER DAY */}

                  <TextField
                    fullWidth
                    size="small"
                    type="number"
                    label="Fine Per Day *"
                    value={form.fine_per_day}
                    disabled={
                      dialogMode === "view" || formLoading
                    }
                    inputProps={{ min: 0, step: "0.01" }}
                    onChange={(e) =>
                      handleFormChange(
                        "fine_per_day",
                        e.target.value
                      )
                    }
                    sx={textFieldSx}
                  />

                  {/* MAXIMUM FINE */}

                  <TextField
                    fullWidth
                    size="small"
                    type="number"
                    label="Maximum Fine *"
                    value={form.maximum_fine}
                    disabled={
                      dialogMode === "view" || formLoading
                    }
                    inputProps={{ min: 0, step: "0.01" }}
                    onChange={(e) =>
                      handleFormChange(
                        "maximum_fine",
                        e.target.value
                      )
                    }
                    sx={textFieldSx}
                  />

                  {/* STATUS (edit only, per Swagger update payload) */}

                  {dialogMode === "edit" && (
                    <FormControl fullWidth size="small">
                      <InputLabel sx={inputLabelSx}>
                        Status
                      </InputLabel>

                      <Select
                        label="Status"
                        value={
                          form.is_active ? "true" : "false"
                        }
                        disabled={formLoading}
                        onChange={(e) =>
                          handleFormChange(
                            "is_active",
                            e.target.value === "true"
                          )
                        }
                        sx={selectSx}
                        MenuProps={menuProps}
                      >
                        <MenuItem value="true">
                          Active
                        </MenuItem>

                        <MenuItem value="false">
                          Inactive
                        </MenuItem>
                      </Select>
                    </FormControl>
                  )}
                </Box>

                {/* ACTIVE SWITCH (edit only) */}

                {dialogMode === "edit" && (
                  <Box
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "space-between",
                      mt: 2,
                      p: 1.5,
                      borderRadius: 2,
                      border: `1px solid ${borderColor}`,
                    }}
                  >
                    <Box>
                      <Typography
                        sx={{
                          color: `${modalTextColor} !important`,
                          fontWeight: 600,
                        }}
                      >
                        Active Status
                      </Typography>

                      <Typography
                        variant="caption"
                        sx={{
                          color: `${secondaryTextColor} !important`,
                          display: "block",
                        }}
                      >
                        Deactivated rules are not applied
                        to new fines.
                      </Typography>
                    </Box>

                    <Switch
                      checked={Boolean(form.is_active)}
                      disabled={formLoading}
                      onChange={(e) =>
                        handleFormChange(
                          "is_active",
                          e.target.checked
                        )
                      }
                      color="success"
                    />
                  </Box>
                )}
              </>
            )}
          </CustomScrollbar>

          {/* FOOTER */}

          <Box
            sx={{
              display: "flex",
              justifyContent: "flex-end",
              gap: 1,
              p: 2,
              borderTop: `1px solid ${borderColor}`,
              flexShrink: 0,
            }}
          >
            <Button
              variant="contained"
              onClick={() => !formLoading && setOpenDialog(false)}
              disabled={formLoading}
              sx={{
                textTransform: "none",
                color: textColor,
                borderColor,
              }}
            >
              CLOSE
            </Button>

            {dialogMode === "create" && (
              <Button
                variant="contained"
                onClick={handleSubmit}
                disabled={formLoading}
                sx={{
                  textTransform: "none",
                  minWidth: 150,
                }}
              >
                {formLoading ? (
                  <CircularProgress size={22} color="inherit" />
                ) : (
                  "CREATE FINE RULE"
                )}
              </Button>
            )}

            {dialogMode === "edit" && (
              <Button
                variant="contained"
                onClick={handleUpdate}
                disabled={formLoading}
                sx={{
                  textTransform: "none",
                  minWidth: 150,
                }}
              >
                {formLoading ? (
                  <CircularProgress size={22} color="inherit" />
                ) : (
                  "UPDATE"
                )}
              </Button>
            )}
          </Box>
        </Box>
      </Modal>

      {/* =====================================================
          DELETE / ACTIVATE / DEACTIVATE MODAL
      ===================================================== */}

      <Modal
        open={actionModalOpen}
        onClose={handleCloseActionModal}
      >
        <Box
          sx={{
            position: "absolute",
            top: "50%",
            left: "50%",
            transform: "translate(-50%, -50%)",
            width: { xs: "92%", sm: 450 },
            maxWidth: "95%",
            bgcolor: theme.palette.background.paper,
            color: modalTextColor,
            borderRadius: 3,
            boxShadow: 24,
            overflow: "hidden",
            outline: "none",
          }}
        >
          {/* ACTION MODAL HEADER */}

          <Box
            sx={{
              backgroundColor:
                actionType === "delete"
                  ? theme.palette.error.main
                  : actionRow?.is_active
                  ? theme.palette.warning.main
                  : theme.palette.success.main,
              px: 2.5,
              py: 1.75,
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
            }}
          >
            <Typography
              variant="h6"
              sx={{
                color: "#ffffff !important",
                fontWeight: 700,
              }}
            >
              {actionType === "delete"
                ? "Delete Fine Rule"
                : actionRow?.is_active
                ? "Deactivate Fine Rule"
                : "Activate Fine Rule"}
            </Typography>

            <IconButton
              disabled={actionLoading}
              onClick={handleCloseActionModal}
              sx={{ color: "#ffffff" }}
            >
              <Close />
            </IconButton>
          </Box>

          {/* ACTION MODAL BODY */}

          <Box sx={{ p: 3 }}>
            <Typography
              variant="body1"
              sx={{
                color: modalTextColor,
                mb: 1,
              }}
            >
              {actionType === "delete"
                ? "Are you sure you want to delete this fine rule?"
                : actionRow?.is_active
                ? "Are you sure you want to deactivate this fine rule?"
                : "Are you sure you want to activate this fine rule?"}
            </Typography>

            {/* SELECTED RULE DETAILS */}

            {actionRow && (
              <Paper
                variant="contained"
                sx={{
                  p: 2,
                  mt: 2,
                  mb: 2,
                  borderRadius: 2,
                  backgroundColor: isDark
                    ? "rgba(255,255,255,0.03)"
                    : "rgba(0,0,0,0.02)",
                  borderColor,
                }}
              >
                <Stack spacing={0.8}>
                  <Typography
                    variant="body2"
                    sx={{ color: secondaryTextColor }}
                  >
                    <strong>Branch:</strong>{" "}
                    {actionRow.branch_name || "-"}
                  </Typography>

                  <Typography
                    variant="body2"
                    sx={{ color: secondaryTextColor }}
                  >
                    <strong>Member Type:</strong>{" "}
                    {actionRow.member_type_display ||
                      actionRow.member_type ||
                      "-"}
                  </Typography>

                  <Typography
                    variant="body2"
                    sx={{ color: secondaryTextColor }}
                  >
                    <strong>Category:</strong>{" "}
                    {actionRow.category_name || "All Categories"}
                  </Typography>

                  <Typography
                    variant="body2"
                    sx={{ color: secondaryTextColor }}
                  >
                    <strong>Fine / Day:</strong> ₹{" "}
                    {actionRow.fine_per_day ?? "0"}
                  </Typography>

                  <Typography
                    variant="body2"
                    sx={{ color: secondaryTextColor }}
                  >
                    <strong>Maximum Fine:</strong> ₹{" "}
                    {actionRow.maximum_fine ?? "0"}
                  </Typography>
                </Stack>
              </Paper>
            )}

            {/* ACTION BUTTONS */}

            <Box
              sx={{
                display: "flex",
                justifyContent: "flex-end",
                gap: 1,
                mt: 3,
              }}
            >
              <Button
                variant="contained"
                disabled={actionLoading}
                onClick={handleCloseActionModal}
                sx={{
                  textTransform: "none",
                  color: textColor,
                  borderColor,
                }}
              >
                CANCEL
              </Button>

              <Button
                variant="contained"
                color={
                  actionType === "delete"
                    ? "error"
                    : actionRow?.is_active
                    ? "warning"
                    : "success"
                }
                disabled={actionLoading}
                onClick={handleConfirmAction}
                sx={{
                  textTransform: "none",
                  minWidth: 140,
                }}
              >
                {actionLoading ? (
                  <CircularProgress size={22} color="inherit" />
                ) : actionType === "delete" ? (
                  "DELETE"
                ) : actionRow?.is_active ? (
                  "DEACTIVATE"
                ) : (
                  "ACTIVATE"
                )}
              </Button>
            </Box>
          </Box>
        </Box>
      </Modal>
    </Box>
  );
}